import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/common/components/Card/Card';
import { CardAction } from '@/common/components/CardAction/CardAction';
import { Divider } from '@/common/components/Divider/Divider';
import { Icon } from '@/common/components/Icons/Icon';
import { Label } from '@/common/components/Label/Label';
import { LabeledValue } from '@/common/components/LabeledValue/LabeledValue';
import { Modal } from '@/common/components/Modal/Modal';
import { SkeletonInline } from '@/common/components/SkeletonInline/SkeletonInline';
import { Stack } from '@/common/components/Stack/Stack';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { IMenhirBenefitSlug } from '@/common/integrations/menhir/employee/benefits/benefits.types';
import { useGetCompensationSpendingBreakdown } from '@/common/integrations/menhir/employee/compensation/compensation.hooks';
import { INTL_MONTH_DAY_SHORT_FORMAT } from '@/common/utils/date';
import { intlFormat } from '@/common/utils/date';
import { MoneyUtils } from '@coverflex-tech/hypernova-utils';

export function SpentOnThisPeriodActivityCard({
  categorySlug,
  from,
  to,
}: TSpentOnThisPeriodActivityCardProps) {
  const { t } = useTranslation();
  const { company } = useEmployeeDetails();
  const { hasSocialBenefits } = company.compensationConfig;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const available =
    hasSocialBenefits &&
    ['meals', 'es-public-transport', 'es-childcare'].includes(categorySlug);
  const { spendingBreakdown, isInitialLoading } =
    useGetCompensationSpendingBreakdown(
      {
        category_slug: categorySlug,
        filters: {
          executed_from: from,
          executed_to: to,
        },
      },
      { enabled: available }
    );

  if (isInitialLoading) {
    return <SkeletonInline />;
  }

  if (!available || !spendingBreakdown) {
    return null;
  }

  return (
    <>
      <CardAction
        icon={<Icon icon="chart-column" color="accentSecondary10" />}
        iconBadgeColor="accentSecondary20"
        title={t('dashboard.activity.spent_on_this_period.card.title', {
          amount: MoneyUtils.formatMoney(spendingBreakdown?.total),
        })}
        action={<Icon icon="chevron-right" color="neutral20" />}
        backgroundColor="neutral10"
        onPress={() => {
          setIsModalOpen(true);
        }}
        size="sm"
      />
      {isModalOpen && (
        <Modal
          onClose={() => setIsModalOpen(false)}
          title={t('dashboard.activity.spent_on_this_period.modal.title')}
          icon={<Icon icon="chart-column" color="accentSecondary10" />}
          iconBadgeColor="accentSecondary20"
        >
          <Card plain>
            <Stack gap="xs">
              <LabeledValue
                orientation="horizontal"
                bold
                label={t(
                  'dashboard.activity.spent_on_this_period.modal.section.total.title'
                )}
                value={MoneyUtils.formatMoney(spendingBreakdown?.total)}
              />
              {Boolean(from && to) && (
                <Label size="sm">
                  {t('general.labels.from_x_to_n', {
                    x: intlFormat(from, INTL_MONTH_DAY_SHORT_FORMAT),
                    n: intlFormat(to, INTL_MONTH_DAY_SHORT_FORMAT),
                  })}
                </Label>
              )}
              <Divider className="my-sm" />
              <Label bold>
                {t(
                  'dashboard.activity.spent_on_this_period.modal.section.amounts.title'
                )}
              </Label>
              <LabeledValue
                orientation="horizontal"
                size="sm"
                label={t('general.labels.accumulated_balance')}
                value={MoneyUtils.formatMoney(spendingBreakdown?.accumulated)}
              />
              {spendingBreakdown?.attributions.map((attribution) => (
                <LabeledValue
                  key={attribution.slug}
                  orientation="horizontal"
                  size="sm"
                  label={t(`general.labels.slug_based.${attribution.slug}`)}
                  value={MoneyUtils.formatMoney(attribution.spendingTotal)}
                />
              ))}
            </Stack>
          </Card>
        </Modal>
      )}
    </>
  );
}

type TSpentOnThisPeriodActivityCardProps = {
  categorySlug: IMenhirBenefitSlug;
  from: string;
  to: string;
};
